import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, fmtNGN, fmtUSD, img } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/MoneyInput";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Users, Image, Star, Gavel, CreditCard, Plus, Pencil, Trash2, ScrollText, TrendingUp, Wallet, Download, ShieldAlert, Megaphone, Search, Trophy, RefreshCw, Activity, AlertTriangle, Clock, Sparkles } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const isLive = cfg && cfg.payment_mode === "live" && cfg.paystack_test_mode === false;

  const statCards = stats ? [
    { icon: Users, label: "Members", value: stats.members },
    { icon: Users, label: "Active Members", value: stats.active_members },
    { icon: Users, label: "New (7d)", value: stats.new_registrations_7d },
    { icon: CreditCard, label: "Revenue (₦)", value: stats.revenue_ngn },
    { icon: Image, label: "Artworks", value: stats.artworks },
    { icon: Image, label: "Holdings", value: stats.holdings },
    { icon: Star, label: "Reviews", value: stats.reviews },
    { icon: Star, label: "Review Rate %", value: stats.review_completion_rate },
    { icon: Gavel, label: "Auctions", value: stats.auctions },
    { icon: Gavel, label: "Auctions Done", value: stats.auctions_completed },
    { icon: Wallet, label: "Reward Wallets ₦", value: stats.reward_wallet_total },
    { icon: TrendingUp, label: "Growth Wallets ₦", value: stats.growth_wallet_total },
    { icon: Download, label: "Withdrawals Pending", value: stats.withdrawals_pending },
    { icon: Download, label: "Withdrawals Done", value: stats.withdrawals_completed },
    { icon: Users, label: "Referrals", value: stats.referrals },
    { icon: ShieldAlert, label: "Fraud Alerts", value: stats.fraud_alerts },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-gold font-bold">Administrator</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mt-1">Control Panel</h1>
        </div>
        {cfg && (
          <span
            data-testid="payment-mode-badge"
            className={`inline-flex items-center gap-1.5 text-xs font-extrabold tracking-wide px-3.5 py-1.5 rounded-full border ${
              isLive
                ? "bg-green-500/15 text-green-600 border-green-500/40"
                : "bg-amber-500/15 text-amber-600 border-amber-500/40"
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${isLive ? "bg-green-500 animate-pulse" : "bg-amber-500"}`} />
            {isLive ? "LIVE MODE · REAL MONEY" : "TEST MODE"}
          </span>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full flex-wrap h-auto">
          <TabsTrigger value="overview" className="rounded-full" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="members" className="rounded-full" data-testid="admin-tab-members">Members</TabsTrigger>
          <TabsTrigger value="trials" className="rounded-full" data-testid="admin-tab-trials">Trials</TabsTrigger>
          <TabsTrigger value="artworks" className="rounded-full" data-testid="admin-tab-artworks">Artworks</TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-full" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="content" className="rounded-full" data-testid="admin-tab-content">Content</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-full" data-testid="admin-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="withdrawals" className="rounded-full" data-testid="admin-tab-withdrawals">Withdrawals</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-full" data-testid="admin-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="logs" className="rounded-full" data-testid="admin-tab-logs">Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          {!stats ? <Loader2 className="h-6 w-6 animate-spin text-gold" /> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statCards.map((s) => (
                <div key={s.label} className="bg-card border border-border rounded-3xl p-5">
                  <s.icon className="h-5 w-5 text-gold" />
                  <p className="font-heading text-2xl font-bold mt-3">{s.value?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="members" className="mt-5"><AdminMembers /></TabsContent>
        <TabsContent value="trials" className="mt-5"><AdminTrials /></TabsContent>
        <TabsContent value="artworks" className="mt-5"><AdminArtworks /></TabsContent>
        <TabsContent value="analytics" className="mt-5"><AdminAnalytics /></TabsContent>
        <TabsContent value="content" className="mt-5"><AdminContent /></TabsContent>
        <TabsContent value="settings" className="mt-5"><AdminSettings /></TabsContent>
        <TabsContent value="withdrawals" className="mt-5"><AdminWithdrawals /></TabsContent>
        <TabsContent value="payments" className="mt-5"><AdminPayments /></TabsContent>
        <TabsContent value="logs" className="mt-5"><AdminLogs /></TabsContent>
      </Tabs>
    </div>
  );
}

function AdminArtworks() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/artworks", { params: { page: p, limit: 20 } });
      setItems(data.items);
      setHasMore(data.has_more); setPage(p);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(1); }, [load]);

  const save = async (form) => {
    try {
      if (editing?.id) {
        await api.patch(`/admin/artworks/${editing.id}`, { price_ngn: Number(form.price_ngn), status: form.status, title: form.title, featured: !!form.featured });
        toast.success("Artwork updated");
      } else {
        await api.post("/admin/artworks", { ...form, price_ngn: Number(form.price_ngn) });
        toast.success("Artwork added");
      }
      setEditing(null); setCreating(false);
      load(1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/artworks/${id}`); toast.success("Artwork removed"); setItems((p) => p.filter((a) => a.id !== id)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="rounded-full navy-gradient text-white font-semibold" onClick={() => setCreating(true)} data-testid="admin-add-artwork"><Plus className="h-4 w-4 mr-2" /> Add Artwork</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((a) => (
          <div key={a.id} className="bg-card border border-border rounded-2xl p-3 flex gap-3" data-testid={`admin-artwork-${a.id}`}>
            <img src={img(a.thumbnail)} alt={a.title} loading="lazy" className="h-20 w-20 rounded-xl object-cover bg-muted" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm truncate">{a.title}</h3>
              <p className="text-xs text-muted-foreground truncate">{a.artist}</p>
              <p className="font-bold text-sm mt-1">{fmtNGN(a.price_ngn)} <span className="text-xs font-normal text-muted-foreground">/ {fmtUSD(a.price_usd)}</span></p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary" data-testid={`admin-tier-${a.id}`}>{a.tier || "—"}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${a.status === "available" ? "bg-gold/15 text-gold" : "bg-secondary text-muted-foreground"}`}>{a.status}</span>
              </div>
              <div className="flex gap-1 mt-2">
                <Button size="sm" variant="outline" className="rounded-full h-7 px-2" onClick={() => setEditing(a)} data-testid={`edit-${a.id}`}><Pencil className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" className="rounded-full h-7 px-2 text-destructive" onClick={() => remove(a.id)} data-testid={`delete-${a.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-3" data-testid="artworks-pagination">
        <Button variant="outline" className="rounded-full" disabled={page <= 1 || loading} onClick={() => load(page - 1)} data-testid="artworks-prev">Previous</Button>
        <span className="text-sm text-muted-foreground">Page {page}</span>
        <Button variant="outline" className="rounded-full" disabled={!hasMore || loading} onClick={() => load(page + 1)} data-testid="artworks-next">Next</Button>
      </div>

      <ArtworkDialog open={creating || !!editing} artwork={editing} onClose={() => { setCreating(false); setEditing(null); }} onSave={save} />
    </div>
  );
}

function ArtworkDialog({ open, artwork, onClose, onSave }) {
  const [form, setForm] = useState({ title: "", artist: "", category: "Painting", description: "", price_ngn: "", image: "", thumbnail: "", status: "available" });
  useEffect(() => {
    if (artwork) setForm({ ...artwork, price_ngn: String(artwork.price_ngn) });
    else setForm({ title: "", artist: "", category: "Painting", description: "", price_ngn: "", image: "", thumbnail: "", status: "available" });
  }, [artwork, open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{artwork ? "Edit Artwork" : "Add Artwork"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={set("title")} className="h-11 rounded-xl mt-1" data-testid="art-title" /></div>
          {!artwork && <>
            <div><Label>Artist</Label><Input value={form.artist} onChange={set("artist")} className="h-11 rounded-xl mt-1" data-testid="art-artist" /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={set("category")} className="h-11 rounded-xl mt-1" /></div>
            <div><Label>Image URL</Label><Input value={form.image} onChange={set("image")} className="h-11 rounded-xl mt-1" placeholder="https://…" data-testid="art-image" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={set("description")} className="h-11 rounded-xl mt-1" /></div>
          </>}
          <div><Label>Price (₦)</Label><MoneyInput value={form.price_ngn} onValueChange={(v) => setForm({ ...form, price_ngn: v })} className="h-11 rounded-xl mt-1" data-testid="art-price" /></div>
          {artwork && (
            <div><Label>Status</Label>
              <div className="flex gap-2 mt-1">
                {["available", "disabled"].map((s) => (
                  <button key={s} type="button" onClick={() => setForm({ ...form, status: s })} className={`flex-1 h-10 rounded-xl text-sm capitalize border ${form.status === s ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`status-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {artwork && (
            <label className="flex items-center justify-between bg-secondary rounded-xl px-4 h-11">
              <span className="text-sm font-medium">Feature on homepage</span>
              <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="art-featured" />
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full navy-gradient text-white font-semibold" onClick={() => onSave(form)} data-testid="save-artwork-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-gold" />;

  const fields = [
    ["daily_review_reward", "Daily Review Reward (₦)"],
    ["max_daily_reviews", "Max Reviews / Day"],
    ["welcome_cashback", "Welcome Cash-back (₦)"],
    ["referral_commission_pct", "Referral Commission (%)"],
    ["max_auction_margin_pct", "Max Auction Margin (%)"],
    ["platform_fee_pct", "Withdrawal Platform Fee (%)"],
    ["tax_pct", "Withdrawal Tax (%)"],
    ["min_withdrawal", "Min Withdrawal (₦)"],
    ["auction_day", "Auction Day (0=Mon..6=Sun)"],
    ["auction_open_hour", "Auction Open Hour (0-23)"],
    ["auction_close_hour", "Auction Close Hour (0-23)"],
    ["auction_eligibility_days", "Auction Eligibility (days)"],
    ["auction_timer_minutes", "Auction Timer (minutes, max 5)"],
    ["ai_bid_intensity", "AI Bidding Intensity (1-5)"],
    ["withdrawal_day", "Withdrawal Day (0=Mon..6=Sun)"],
    ["monthly_collector_bonus", "Monthly Top Collector Bonus (₦)"],
    ["monthly_referrer_bonus", "Monthly Top Referrer Bonus (₦)"],
  ];

  const payLeaders = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/admin/pay-monthly-leaders");
      const lines = (data.results || []).map((r) => `${r.role}: @${r.username} +${fmtNGN(r.amount)}`).join(" · ");
      toast.success(`Paid ${data.month} leaders — ${lines}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPaying(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(fields.map(([k]) => [k, Number(s[k])]));
      payload.auction_test_mode = !!s.auction_test_mode;
      payload.auction_margin_tiers = (s.auction_margin_tiers || []).map((t) => ({
        min: Number(t.min), max: Number(t.max), pct: Number(t.pct),
      }));
      payload.withdrawal_tiers = (s.withdrawal_tiers || [])
        .map((t) => Number(t))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b);
      const { data } = await api.put("/admin/settings", payload);
      setS(data);
      toast.success("Settings updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setTier = (i, key) => (e) => {
    const tiers = [...(s.auction_margin_tiers || [])];
    tiers[i] = { ...tiers[i], [key]: e.target.value };
    setS({ ...s, auction_margin_tiers: tiers });
  };
  const setTierV = (i, key) => (v) => {
    const tiers = [...(s.auction_margin_tiers || [])];
    tiers[i] = { ...tiers[i], [key]: v };
    setS({ ...s, auction_margin_tiers: tiers });
  };

  const setWithdrawTier = (i) => (v) => {
    const tiers = [...(s.withdrawal_tiers || [])];
    tiers[i] = v;
    setS({ ...s, withdrawal_tiers: tiers });
  };
  const addWithdrawTier = () => setS({ ...s, withdrawal_tiers: [...(s.withdrawal_tiers || []), ""] });
  const removeWithdrawTier = (i) => setS({ ...s, withdrawal_tiers: (s.withdrawal_tiers || []).filter((_, idx) => idx !== i) });

  return (
    <div className="max-w-2xl space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map(([k, label]) => (
          <div key={k}>
            <Label>{label}</Label>
            {label.includes("₦") ? (
              <MoneyInput value={s[k]} onValueChange={(v) => setS({ ...s, [k]: v })} className="h-11 rounded-xl mt-1" data-testid={`setting-${k}`} />
            ) : (
              <Input type="number" min={0} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="h-11 rounded-xl mt-1" data-testid={`setting-${k}`} />
            )}
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-testid="margin-tiers">
        <div>
          <p className="font-semibold text-sm">Dynamic Auction Margin Tiers</p>
          <p className="text-xs text-muted-foreground">Profit margin (%) applied by the AI Market Engine, by artwork price band (₦).</p>
        </div>
        {(s.auction_margin_tiers || []).map((t, i) => (
          <div key={i} className="grid grid-cols-3 gap-3" data-testid={`margin-tier-${i}`}>
            <div><Label className="text-xs">Min (₦)</Label><MoneyInput value={t.min} onValueChange={setTierV(i, "min")} className="h-10 rounded-xl mt-1" data-testid={`tier-${i}-min`} /></div>
            <div><Label className="text-xs">Max (₦)</Label><MoneyInput value={t.max} onValueChange={setTierV(i, "max")} className="h-10 rounded-xl mt-1" data-testid={`tier-${i}-max`} /></div>
            <div><Label className="text-xs">Margin (%)</Label><Input type="number" value={t.pct} onChange={setTier(i, "pct")} className="h-10 rounded-xl mt-1" data-testid={`tier-${i}-pct`} /></div>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-testid="withdrawal-tiers">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-sm">Withdrawal Amount Tiers</p>
            <p className="text-xs text-muted-foreground">The fixed withdrawal amounts (₦) members can choose from in the Reward Wallet withdrawal dropdown.</p>
          </div>
          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={addWithdrawTier} data-testid="add-withdraw-tier">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {(s.withdrawal_tiers || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`withdraw-tier-row-${i}`}>
              <MoneyInput value={t} onValueChange={setWithdrawTier(i)} className="h-10 rounded-xl" data-testid={`withdraw-tier-${i}`} />
              <button onClick={() => removeWithdrawTier(i)} className="text-muted-foreground hover:text-red-500 shrink-0" data-testid={`remove-withdraw-tier-${i}`} aria-label="Remove tier">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        {(s.withdrawal_tiers || []).length === 0 && (
          <p className="text-xs text-muted-foreground">No tiers set — members won't be able to withdraw. Add at least one amount.</p>
        )}
      </div>

      <div className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between">
        <div>
          <p className="font-semibold text-sm">Auction Test Mode</p>
          <p className="text-xs text-muted-foreground">Bypass the Wednesday window & Thursday withdrawal rule (for demos/testing).</p>
        </div>
        <button onClick={() => setS({ ...s, auction_test_mode: !s.auction_test_mode })} data-testid="setting-test-mode"
          className={`h-7 w-12 rounded-full transition-colors relative ${s.auction_test_mode ? "navy-gradient" : "bg-secondary"}`}>
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${s.auction_test_mode ? "right-1" : "left-1"}`} />
        </button>
      </div>
      <Button className="rounded-full navy-gradient text-white font-semibold" onClick={save} disabled={busy} data-testid="save-settings-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}
      </Button>

      <div className="bg-card border border-border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="leader-rewards">
        <div>
          <p className="font-semibold text-sm flex items-center gap-2"><Trophy className="h-4 w-4 text-gold" /> Monthly Leader Rewards</p>
          <p className="text-xs text-muted-foreground">Credit this month's #1 Collector and #1 Referrer their configured Reward Wallet bonus. Once per month.</p>
        </div>
        <Button className="rounded-full bg-gold text-gold-foreground hover:bg-gold/90 font-semibold shrink-0" onClick={payLeaders} disabled={paying} data-testid="pay-leaders-btn">
          {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pay Monthly Leaders"}
        </Button>
      </div>
    </div>
  );
}

function AdminWithdrawals() {
  const [items, setItems] = useState(null);
  const [changes, setChanges] = useState([]);
  const load = () => {
    api.get("/admin/withdrawals").then((r) => setItems(r.data.items));
    api.get("/admin/bank-change-requests").then((r) => setChanges(r.data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const process = async (id, action) => {
    try { await api.post(`/admin/withdrawals/${id}/${action}`); toast.success(`Withdrawal ${action}d`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const processChange = async (memberId, action) => {
    try { await api.post(`/admin/bank-change-requests/${memberId}/${action}`); toast.success(`Account change ${action}d`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!items) return <Loader2 className="h-6 w-6 animate-spin text-gold" />;

  return (
    <div className="space-y-4">
      {changes.length > 0 && (
        <div className="space-y-2" data-testid="bank-change-requests">
          <p className="font-heading font-bold text-sm">Receiving-Account Change Requests ({changes.length})</p>
          {changes.map((c) => (
            <div key={c.member_id} className="bg-card border border-gold/30 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid={`bank-change-${c.member_id}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold text-sm">{c.username}</p>
                <p className="text-xs text-muted-foreground">From: {c.current ? `${c.current.bank_name} ${c.current.account_number_masked}` : "—"}</p>
                <p className="text-xs text-muted-foreground">To: {c.requested.bank_name} {c.requested.account_number_masked} • {c.requested.account_name}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="rounded-full navy-gradient text-white" onClick={() => processChange(c.member_id, "approve")} data-testid={`approve-change-${c.member_id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => processChange(c.member_id, "reject")} data-testid={`reject-change-${c.member_id}`}>Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 ? <p className="text-sm text-muted-foreground">No withdrawal requests yet.</p> : (
      <div className="space-y-2">
      {items.map((w) => (
        <div key={w.id} className="bg-card border border-border rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid={`withdrawal-${w.id}`}>
          <div className="flex-1 min-w-[180px]">
            <p className="font-semibold text-sm flex items-center gap-2">{w.username} — {fmtNGN(w.requested)}
              {w.type === "opt_out" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-destructive/10 text-destructive" data-testid={`optout-tag-${w.id}`}>OPT-OUT · CLOSES ACCOUNT</span>}
            </p>
            <p className="text-xs text-muted-foreground">Net {fmtNGN(w.net_amount)} → {w.bank_account?.bank_name} {w.bank_account?.account_number_masked || ("••••" + (w.bank_account?.last4 || ""))}</p>
            {w.type === "opt_out" && <p className="text-[11px] text-muted-foreground">Reward ₦{Number(w.reward_portion || 0).toLocaleString()} + Growth ₦{Number(w.growth_portion || 0).toLocaleString()} • Approving transfers net payout & permanently closes this account.</p>}
            <p className="text-[10px] text-muted-foreground">{new Date(w.created_at).toLocaleString()}</p>
          </div>
          {w.status === "pending" ? (
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full navy-gradient text-white" onClick={() => process(w.id, "approve")} data-testid={`approve-${w.id}`}>Approve</Button>
              <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => process(w.id, "reject")} data-testid={`reject-${w.id}`}>Reject</Button>
            </div>
          ) : (
            <span className={`text-xs font-bold px-3 py-1 rounded-full capitalize ${["approved", "processing", "successful"].includes(w.status) ? "bg-gold/15 text-gold" : "bg-destructive/10 text-destructive"}`}>{w.status}</span>
          )}
        </div>
      ))}
      </div>
      )}
    </div>
  );
}

function AdminTrials() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    api.get(`/admin/trials?page=${page}`).then((r) => setData(r.data)).catch(() => {});
  }, [page]);
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-gold" />;
  const f = data.funnel;
  const fmtLeft = (s) => {
    if (!s || s <= 0) return "expiring";
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600);
    return d >= 1 ? `${d}d ${h}h` : `${h}h ${Math.floor((s % 3600) / 60)}m`;
  };
  const cards = [
    { label: "Trials Started", value: f.started, icon: Sparkles },
    { label: "Active Now", value: f.active, icon: Clock },
    { label: "Converted", value: f.converted, icon: TrendingUp },
    { label: "Expired", value: f.expired, icon: Trash2 },
  ];
  return (
    <div className="space-y-6" data-testid="admin-trials">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-border rounded-3xl p-5">
            <c.icon className="h-5 w-5 text-gold" />
            <p className="font-heading text-2xl font-bold mt-3" data-testid={`trial-stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>{(c.value ?? 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-3xl p-5" data-testid="trial-funnel">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading text-base font-bold">Started → Converted Funnel</p>
          <span className="text-sm font-bold text-gold">{f.conversion_rate}% conversion</span>
        </div>
        <div className="h-3 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-gold rounded-full transition-all" style={{ width: `${Math.min(100, f.conversion_rate)}%` }} />
        </div>
        <p className="text-xs text-muted-foreground mt-2">{f.converted} of {f.started} trials became paying members · {f.active} still exploring · {f.expired} expired without paying</p>
      </div>

      <div className="bg-card border border-border rounded-3xl overflow-hidden">
        <div className="px-5 py-3 border-b border-border font-semibold text-sm">Active Trials ({data.total})</div>
        {data.items.length === 0 ? (
          <p className="p-6 text-center text-muted-foreground text-sm" data-testid="no-trials">No active trials right now.</p>
        ) : (
          <div className="divide-y divide-border">
            {data.items.map((t) => (
              <div key={t.id} className="px-5 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`trial-row-${t.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">@{t.username}</p>
                  <p className="text-xs text-muted-foreground">{t.phone} · started {new Date(t.trial_start_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs">Reward: {fmtNGN(t.reward_wallet || 0)}</p>
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold ${t.seconds_left < 86400 ? "text-amber-600" : "text-muted-foreground"}`}>
                    <Clock className="h-3 w-3" /> {fmtLeft(t.seconds_left)} left
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        {(page > 1 || data.has_more) && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="text-sm disabled:opacity-40" data-testid="trials-prev">← Prev</button>
            <span className="text-xs text-muted-foreground">Page {page}</span>
            <button disabled={!data.has_more} onClick={() => setPage((p) => p + 1)} className="text-sm disabled:opacity-40" data-testid="trials-next">Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}


function AdminMembers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const load = async (query = "", p = 1) => {
    setLoading(true);
    try { const { data } = await api.get("/admin/members", { params: { q: query || undefined, page: p, limit: 20 } }); setItems(data.items); setHasMore(data.has_more); setPage(p); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/members/${id}/status`, { status }); toast.success(`Member ${status}`); load(q, page); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const archiveMember = async (id) => {
    try { await api.post(`/admin/members/${id}/archive`); toast.success("Member archived"); load(q, page); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteMember = async (id) => {
    if (!window.confirm("Permanently delete this member and all their data? This cannot be undone.")) return;
    try { await api.delete(`/admin/members/${id}`); toast.success("Member deleted"); load(q, page); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openDetail = async (id) => {
    try { const { data } = await api.get(`/admin/members/${id}`); setDetail(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  const doReset = async () => {
    try { await api.post(`/admin/members/${resetFor}/reset-password`, { type: "login", new_password: newPw }); toast.success("Login password reset"); setResetFor(null); setNewPw(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resetWithdrawalPw = async (id) => {
    try { await api.post(`/admin/members/${id}/reset-withdrawal-password`); toast.success("Withdrawal password reset — member must set a new one"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={(e) => { e.preventDefault(); load(q, 1); }} className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username, phone, member ID…" className="h-11 rounded-full pl-10" data-testid="member-search" />
      </form>
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-gold" /> : (
        <div className="space-y-2">
          {items.map((m) => (
            <div key={m.id} className="bg-card border border-border rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid={`member-${m.id}`}>
              <div className="flex-1 min-w-[160px]">
                <p className="font-semibold text-sm">{m.username} <span className="text-xs text-muted-foreground">({m.member_id})</span></p>
                <p className="text-xs text-muted-foreground">{m.phone} • R:{fmtNGN(m.reward_wallet)} G:{fmtNGN(m.growth_wallet)}</p>
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full capitalize ${m.status === "active" ? "bg-gold/15 text-gold" : m.status === "pending_payment" ? "bg-secondary text-muted-foreground" : "bg-destructive/10 text-destructive"}`}>{m.status.replace("_", " ")}</span>
              <div className="flex gap-1 flex-wrap">
                <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => openDetail(m.id)} data-testid={`view-${m.id}`}>View</Button>
                {m.status !== "suspended" ? (
                  <Button size="sm" variant="outline" className="rounded-full h-8 text-destructive" onClick={() => setStatus(m.id, "suspended")} data-testid={`suspend-${m.id}`}>Suspend</Button>
                ) : (
                  <Button size="sm" variant="outline" className="rounded-full h-8 text-gold" onClick={() => setStatus(m.id, "active")} data-testid={`reactivate-${m.id}`}>Reactivate</Button>
                )}
                <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setResetFor(m.id)} data-testid={`reset-${m.id}`}>Reset Login PW</Button>
                <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => resetWithdrawalPw(m.id)} data-testid={`reset-wd-${m.id}`}>Reset Fund PW</Button>
                <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => archiveMember(m.id)} data-testid={`archive-${m.id}`}>Archive</Button>
                <Button size="sm" variant="outline" className="rounded-full h-8 text-destructive" onClick={() => deleteMember(m.id)} data-testid={`delete-${m.id}`}>Delete</Button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-muted-foreground">No members found.</p>}
          <div className="flex items-center justify-center gap-3 pt-2" data-testid="members-pagination">
            <Button variant="outline" className="rounded-full" disabled={page <= 1} onClick={() => load(q, page - 1)} data-testid="members-prev">Previous</Button>
            <span className="text-sm text-muted-foreground">Page {page}</span>
            <Button variant="outline" className="rounded-full" disabled={!hasMore} onClick={() => load(q, page + 1)} data-testid="members-next">Next</Button>
          </div>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{detail?.member?.username}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">{detail.member.member_id} • {detail.member.phone} • {detail.member.status}</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-secondary rounded-xl p-3"><p className="text-xs text-muted-foreground">Reward</p><p className="font-bold">{fmtNGN(detail.member.reward_wallet)}</p></div>
                <div className="bg-secondary rounded-xl p-3"><p className="text-xs text-muted-foreground">Growth</p><p className="font-bold">{fmtNGN(detail.member.growth_wallet)}</p></div>
                <div className="bg-secondary rounded-xl p-3"><p className="text-xs text-muted-foreground">Artworks</p><p className="font-bold">{detail.holdings.length}</p></div>
                <div className="bg-secondary rounded-xl p-3"><p className="text-xs text-muted-foreground">Reviews</p><p className="font-bold">{detail.review_count}</p></div>
                <div className="bg-secondary rounded-xl p-3"><p className="text-xs text-muted-foreground">Referrals</p><p className="font-bold">{detail.referrals.length}</p></div>
                <div className="bg-secondary rounded-xl p-3"><p className="text-xs text-muted-foreground">Withdrawals</p><p className="font-bold">{detail.withdrawals.length}</p></div>
              </div>
              <div className="bg-secondary rounded-xl p-3" data-testid="member-receiving-account">
                <p className="text-xs text-muted-foreground">Receiving Account — <span className="font-semibold">{detail.member.bank_status || "NOT_SETUP"}</span></p>
                {detail.member.bank_account ? (
                  <p className="font-medium">{detail.member.bank_account.bank_name} • {detail.member.bank_account.account_number_masked} • {detail.member.bank_account.account_name}</p>
                ) : <p className="text-muted-foreground">Not set up</p>}
                {detail.member.withdrawal_locked && <p className="text-xs text-destructive mt-1">Withdrawals temporarily locked (failed fund-password attempts)</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(o) => { if (!o) setResetFor(null); }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading">Reset member password</DialogTitle><DialogDescription>Set a new temporary password for this member.</DialogDescription></DialogHeader>
          <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (min 6 chars)" className="h-11 rounded-xl" data-testid="reset-password-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setResetFor(null)}>Cancel</Button>
            <Button className="rounded-full navy-gradient text-white font-semibold" onClick={doReset} data-testid="confirm-reset-btn">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Bars({ data, label }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="bg-card border border-border rounded-3xl p-5">
      <p className="font-heading font-bold mb-3">{label}</p>
      <div className="flex items-end gap-1 h-32">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="w-full rounded-t-md navy-gradient transition-all" style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? 4 : 1 }} title={`${d.date}: ${d.count}`} />
            <span className="text-[8px] text-muted-foreground rotate-0">{d.date}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminAnalytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  const dl = (what, fmt = "csv") => {
    const url = `${api.defaults.baseURL}/admin/export/${what}?fmt=${fmt}`;
    const token = localStorage.getItem("arc_token");
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => r.blob()).then((b) => {
      const u = URL.createObjectURL(b); const link = document.createElement("a"); link.href = u; link.download = `arc_${what}.${fmt}`; link.click(); URL.revokeObjectURL(u);
    });
  };
  if (!a) return <Loader2 className="h-6 w-6 animate-spin text-gold" />;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-full" onClick={() => dl("analytics", "pdf")} data-testid="export-analytics-pdf"><Download className="h-4 w-4 mr-2" /> Analytics PDF</Button>
        <Button variant="outline" className="rounded-full" onClick={() => dl("analytics", "csv")} data-testid="export-analytics-csv"><Download className="h-4 w-4 mr-2" /> Analytics CSV</Button>
        <Button variant="outline" className="rounded-full" onClick={() => dl("members", "csv")} data-testid="export-members"><Download className="h-4 w-4 mr-2" /> Members CSV</Button>
        <Button variant="outline" className="rounded-full" onClick={() => dl("members", "pdf")} data-testid="export-members-pdf"><Download className="h-4 w-4 mr-2" /> Members PDF</Button>
        <Button variant="outline" className="rounded-full" onClick={() => dl("withdrawals", "csv")} data-testid="export-withdrawals"><Download className="h-4 w-4 mr-2" /> Withdrawals CSV</Button>
        <Button variant="outline" className="rounded-full" onClick={() => dl("withdrawals", "pdf")} data-testid="export-withdrawals-pdf"><Download className="h-4 w-4 mr-2" /> Withdrawals PDF</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Bars data={a.registrations} label="Daily Registrations (14d)" />
        <Bars data={a.purchases} label="Daily Purchases (14d)" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-3xl p-5">
          <p className="font-heading font-bold mb-3">Most Reviewed</p>
          {a.top_reviewed.length ? a.top_reviewed.map((t, i) => <div key={i} className="flex justify-between text-sm py-1"><span className="truncate mr-2">{t.title}</span><span className="font-bold text-gold">{t.count}</span></div>) : <p className="text-sm text-muted-foreground">No data yet.</p>}
        </div>
        <div className="bg-card border border-border rounded-3xl p-5">
          <p className="font-heading font-bold mb-3">Most Purchased</p>
          {a.top_purchased.length ? a.top_purchased.map((t, i) => <div key={i} className="flex justify-between text-sm py-1"><span className="truncate mr-2">{t.title}</span><span className="font-bold text-gold">{t.count}</span></div>) : <p className="text-sm text-muted-foreground">No data yet.</p>}
        </div>
      </div>
    </div>
  );
}

function AdminContent() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", kind: "announcement", broadcast: false });
  const load = () => api.get("/admin/announcements").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title || !form.body) { toast.error("Title and body required"); return; }
    try { await api.post("/admin/announcements", { ...form, active: true }); toast.success("Published"); setForm({ title: "", body: "", kind: "announcement", broadcast: false }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/announcements/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-card border border-border rounded-3xl p-5 space-y-3">
        <p className="font-heading font-bold flex items-center gap-2"><Megaphone className="h-4 w-4 text-gold" /> New Announcement</p>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="h-11 rounded-xl" data-testid="ann-title" />
        <Input value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Message" className="h-11 rounded-xl" data-testid="ann-body" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.broadcast} onChange={(e) => setForm({ ...form, broadcast: e.target.checked })} data-testid="ann-broadcast" /> Also send as notification to all members</label>
        <Button className="rounded-full navy-gradient text-white font-semibold" onClick={create} data-testid="publish-ann-btn"><Plus className="h-4 w-4 mr-2" /> Publish</Button>
      </div>
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between" data-testid={`ann-${a.id}`}>
            <div><p className="font-semibold text-sm">{a.title}</p><p className="text-xs text-muted-foreground">{a.body}</p></div>
            <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => del(a.id)} data-testid={`del-ann-${a.id}`}><Trash2 className="h-3 w-3" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground">No announcements yet.</p>}
      </div>
    </div>
  );
}

function AdminLogs() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get("/admin/logs").then((r) => setLogs(r.data.logs)); }, []);
  if (!logs) return <Loader2 className="h-6 w-6 animate-spin text-gold" />;
  if (!logs.length) return <p className="text-sm text-muted-foreground">No admin activity yet.</p>;
  return (
    <div className="space-y-2">
      {logs.map((l) => (
        <div key={l.id} className="bg-card border border-border rounded-2xl p-4 flex items-start gap-3">
          <ScrollText className="h-4 w-4 text-gold mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{l.action.replace(/_/g, " ")}</p>
            <p className="text-xs text-muted-foreground truncate">{JSON.stringify(l.detail)}</p>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}


function AdminPayments() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [purpose, setPurpose] = useState("");
  const [status, setStatus] = useState("");
  const [detail, setDetail] = useState(null);
  const [recon, setRecon] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const loadHealth = () => {
    setHealthLoading(true);
    api.get("/admin/payments/payout-health")
      .then((r) => setHealth(r.data))
      .catch(() => {})
      .finally(() => setHealthLoading(false));
  };

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (purpose) params.set("purpose", purpose);
    if (status) params.set("status", status);
    api.get(`/admin/payments?${params.toString()}`).then((r) => setItems(r.data.items));
  };
  useEffect(() => {
    api.get("/admin/payments/stats").then((r) => setStats(r.data));
    api.get("/admin/reconciliation").then((r) => setRecon(r.data)).catch(() => {});
    loadHealth();
    load();
    const healthTimer = setInterval(loadHealth, 60000);
    return () => clearInterval(healthTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [purpose, status]);

  const badge = (s) => ({
    success: "bg-green-500/15 text-green-600", successful: "bg-green-500/15 text-green-600",
    pending: "bg-amber-500/15 text-amber-600", processing: "bg-blue-500/15 text-blue-600",
    failed: "bg-destructive/15 text-destructive", reversed: "bg-destructive/15 text-destructive",
  }[s] || "bg-secondary text-muted-foreground");

  if (!stats) return <Loader2 className="h-6 w-6 animate-spin text-gold" />;
  const cards = [
    ["Membership Collected", fmtNGN(stats.membership_total), `${stats.membership_count} payments`],
    ["Growth Funding", fmtNGN(stats.growth_total), `${stats.growth_count} payments`],
    ["Cashback Issued", fmtNGN(stats.cashback_issued), "Reward Wallet"],
    ["Withdrawals", fmtNGN(stats.withdrawals_total), `${stats.withdrawals_success} paid / ${stats.withdrawals_failed} failed`],
    ["Platform Fees", fmtNGN(stats.platform_fees), "collected"],
    ["Taxes", fmtNGN(stats.taxes), "collected"],
    ["Failed Payments", stats.failed_payments, "count"],
    ["Pending Payments", stats.pending_payments, "count"],
  ];

  return (
    <div className="space-y-5" data-testid="admin-payments">
      <div className="flex items-center gap-3 flex-wrap">
        {stats.test_mode && (
          <span className="text-xs font-bold px-3 py-1 rounded-full bg-amber-500/15 text-amber-600 border border-amber-500/30" data-testid="paystack-test-badge">
            PAYSTACK TEST MODE
          </span>
        )}
        <span className="text-xs text-muted-foreground">Gateway: Paystack ({stats.payment_mode})</span>
      </div>

      {health && (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid="payout-health">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-gold" />
              <p className="font-heading text-base font-bold">Payout Health</p>
            </div>
            <button onClick={loadHealth} disabled={healthLoading}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              data-testid="payout-health-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${healthLoading ? "animate-spin" : ""}`} /> Refresh
              <span className="hidden sm:inline text-[10px] opacity-70">· auto every 60s</span>
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-2xl border border-gold/30 bg-gold/5 p-4" data-testid="payout-balance">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Paystack Balance</div>
              {health.balance_ngn !== null && health.balance_ngn !== undefined ? (
                <p className="font-heading text-2xl font-bold mt-1 text-gold" data-testid="payout-balance-value">{fmtNGN(health.balance_ngn)}</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-2" data-testid="payout-balance-error">{health.balance_error || "Unavailable"}</p>
              )}
              <p className="text-[11px] text-muted-foreground mt-1">Available to pay out</p>
            </div>

            <div className="rounded-2xl border border-border p-4" data-testid="payout-success-rate">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> Transfer Success Rate</div>
              {health.success_rate !== null && health.success_rate !== undefined ? (
                <p className={`font-heading text-2xl font-bold mt-1 ${health.success_rate >= 95 ? "text-green-600" : health.success_rate >= 80 ? "text-amber-600" : "text-destructive"}`}>{health.success_rate}%</p>
              ) : (
                <p className="font-heading text-2xl font-bold mt-1 text-muted-foreground">—</p>
              )}
              <p className="text-[11px] text-muted-foreground mt-1">{health.transfers_success} paid / {health.transfers_failed} failed</p>
            </div>

            <div className="rounded-2xl border border-border p-4" data-testid="payout-processing">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5" /> In Progress</div>
              <p className="font-heading text-2xl font-bold mt-1 text-blue-600">{health.transfers_processing}</p>
              <p className="text-[11px] text-muted-foreground mt-1">transfers settling</p>
            </div>

            <div className="rounded-2xl border border-border p-4" data-testid="payout-pending-approval">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><CreditCard className="h-3.5 w-3.5" /> Awaiting Approval</div>
              <p className={`font-heading text-2xl font-bold mt-1 ${health.pending_approval > 0 ? "text-amber-600" : ""}`}>{health.pending_approval}</p>
              <p className="text-[11px] text-muted-foreground mt-1">withdrawals to review</p>
            </div>
          </div>

          {health.recent_failures && health.recent_failures.length > 0 && (
            <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/5 p-4" data-testid="payout-failures">
              <p className="font-semibold text-sm text-destructive flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Failed payouts ({health.recent_failures.length})</p>
              <div className="mt-2 space-y-1">
                {health.recent_failures.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-xs" data-testid={`payout-failure-${i}`}>
                    <span className="text-foreground">@{f.username || "member"} · {fmtNGN(f.amount || 0)}</span>
                    <span className="text-muted-foreground">{f.status}{f.error ? ` — ${f.error}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map(([label, value, sub]) => (
          <div key={label} className="bg-card border border-border rounded-2xl p-4">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="font-heading text-xl font-bold mt-1">{typeof value === "number" ? value.toLocaleString() : value}</p>
            <p className="text-[11px] text-muted-foreground">{sub}</p>
          </div>
        ))}
      </div>

      {recon && recon.count > 0 && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-2xl p-4" data-testid="reconciliation-flags">
          <p className="font-semibold text-sm text-destructive flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Reconciliation flags ({recon.count})</p>
          <div className="mt-2 space-y-1">
            {recon.flags.map((f, i) => (
              <p key={i} className="text-xs text-muted-foreground">{f.type} — {f.reference || f.username || ""}</p>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference / member / Paystack ID" className="h-10 rounded-full pl-9" data-testid="payment-search" />
        </form>
        <select value={purpose} onChange={(e) => setPurpose(e.target.value)} className="h-10 rounded-full border border-border bg-background px-3 text-sm" data-testid="filter-purpose">
          <option value="">All purposes</option>
          <option value="membership">Membership</option>
          <option value="growth_funding">Growth funding</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-full border border-border bg-background px-3 text-sm" data-testid="filter-status">
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="space-y-2">
        {items.length === 0 ? <p className="text-sm text-muted-foreground">No transactions found.</p> : items.map((p) => (
          <button key={p.reference} onClick={() => api.get(`/admin/payments/${p.reference}`).then((r) => setDetail(r.data)).catch(() => setDetail(p))} data-testid={`payment-row-${p.reference}`}
            className="w-full text-left bg-card border border-border rounded-2xl p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{p.logical_account} • @{p.username}</p>
              <p className="text-xs text-muted-foreground truncate">{p.reference}</p>
            </div>
            <span className="font-bold shrink-0">{fmtNGN(p.amount)}</span>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 ${badge(p.status)}`}>{p.status}</span>
          </button>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="payment-detail">
          <DialogHeader><DialogTitle className="font-heading">Transaction Detail</DialogTitle><DialogDescription>Full audit trail for this Paystack transaction.</DialogDescription></DialogHeader>
          {detail && (
            <div className="space-y-1.5 text-sm">
              {[
                ["AR Club Reference", detail.reference], ["Paystack Transaction ID", detail.paystack_transaction_id || "—"],
                ["Logical Account", detail.logical_account], ["Member", `@${detail.username} (${detail.member_id || "—"})`],
                ["Purpose", detail.purpose], ["Amount", fmtNGN(detail.amount)], ["Currency", detail.currency],
                ["Status", detail.status], ["Verification", detail.verification_status], ["Webhook", detail.webhook_status],
                ["Channel", detail.channel || "—"], ["Test Mode", String(detail.test_mode)],
                ["Created", new Date(detail.created_at).toLocaleString()],
                ["Paid At", detail.paid_at ? new Date(detail.paid_at).toLocaleString() : "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-border/50 py-1">
                  <span className="text-muted-foreground">{k}</span><span className="font-medium text-right break-all">{v}</span>
                </div>
              ))}
              {detail.ledger_entries?.length > 0 && (
                <div className="pt-2">
                  <p className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">Wallet Ledger</p>
                  {detail.ledger_entries.map((l) => (
                    <div key={l.id} className="flex justify-between text-xs py-1">
                      <span>{l.type} ({l.direction})</span>
                      <span>{fmtNGN(l.amount)} • {fmtNGN(l.balance_before)}→{fmtNGN(l.balance_after)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
